<script lang="ts">
	import { onMount } from 'svelte';
	import TableOfContents from '$lib/components/docs/TableOfContents.svelte';
	import { processMarkdownWithOS, extractTableOfContentsWithOS } from '$lib/utils/markdown-enhanced';
	import { initializeCopyButtons } from '$lib/utils/markdown';
	import { initializeEnhancedCodeBlocks } from '$lib/utils/enhanced-code-blocks';
	import '$lib/styles/markdown.css';
	import '$lib/styles/os-code-blocks.css';
	import '$lib/styles/enhanced-code-blocks.css';

	let { data } = $props();

	const slug = $derived(data.slug);
	const content = $derived(data.content);

	// Strip the leading "# Title" so the page header isn't duplicated in the body.
	const bodyContent = $derived(content.content.replace(/^﻿?\s*#\s+[^\n]*\r?\n+/, ''));
	const processedContent = $derived(processMarkdownWithOS(bodyContent));
	const tableOfContents = $derived(extractTableOfContentsWithOS(content.content));

	onMount(() => {
		const timeoutId = setTimeout(() => {
			initializeCopyButtons();
			initializeEnhancedCodeBlocks();
		}, 0);
		return () => clearTimeout(timeoutId);
	});
</script>

<svelte:head>
	<title>{content.title} - Guides - Heyweek</title>
	<meta name="description" content={content.description} />

	<meta property="og:title" content={`${content.title} - Heyweek Guides`} />
	<meta property="og:description" content={content.description} />
	<meta property="og:type" content="article" />
	<meta property="og:url" content={`https://heyweek.com/guides/${slug}`} />

	{@html `<script type="application/ld+json">
	{
		"@context": "https://schema.org",
		"@type": "Article",
		"headline": "${content.title}",
		"description": "${content.description}",
		"dateModified": "${content.lastUpdated}",
		"author": { "@type": "Organization", "name": "Heyweek" },
		"publisher": { "@type": "Organization", "name": "Heyweek" }
	}
	</script>`}
</svelte:head>

<div class="doc-page">
	<div class="doc-page__container">
		<article class="doc-page__content">
			<header class="doc-page__header">
				<h1 class="doc-page__title">{content.title}</h1>
				<p class="doc-page__description">{content.description}</p>
			</header>

			<div class="doc-page__body">
				<div class="doc-page__prose">
					{@html processedContent}
				</div>
			</div>
		</article>

		{#if tableOfContents.length > 0}
			<TableOfContents
				items={tableOfContents.map((item) => ({
					title: item.text,
					anchor: item.anchor,
					level: item.level
				}))}
			/>
		{/if}
	</div>
</div>

<style>
	.doc-page {
		flex: 1;
		min-width: 0;
	}

	.doc-page__container {
		display: flex;
		gap: 3rem;
		max-width: 1400px;
		margin: 0 auto;
		position: relative;
	}

	.doc-page__content {
		flex: 1;
		min-width: 0;
		max-width: 800px;
	}

	.doc-page__header {
		margin-bottom: 2rem;
		padding-bottom: 1.5rem;
		border-bottom: 1px solid var(--border-light);
	}

	.doc-page__title {
		font-size: 2rem;
		font-weight: 600;
		color: var(--text-primary);
		margin-bottom: 1rem;
		line-height: 1.3;
		letter-spacing: -0.01em;
	}

	.doc-page__description {
		font-size: 1rem;
		color: var(--text-secondary);
		line-height: 1.5;
	}

	.doc-page__body {
		margin-bottom: 2rem;
	}

	.doc-page__prose {
		color: var(--text-primary);
		line-height: 1.7;
	}

	.doc-page__prose :global(h2) {
		font-size: 1.375rem;
		font-weight: 600;
		margin: 2rem 0 0.75rem;
		color: var(--text-primary);
		line-height: 1.4;
	}

	.doc-page__prose :global(h3) {
		font-size: 1.125rem;
		font-weight: 600;
		margin: 1.75rem 0 0.5rem;
		color: var(--text-primary);
	}

	.doc-page__prose :global(p) {
		margin: 1rem 0;
		color: var(--text-secondary);
		font-size: 0.9375rem;
	}

	.doc-page__prose :global(ul),
	.doc-page__prose :global(ol) {
		margin: 1rem 0;
		padding-left: 1.5rem;
		color: var(--text-secondary);
	}

	.doc-page__prose :global(li) {
		margin: 0.5rem 0;
		font-size: 0.9375rem;
	}

	.doc-page__prose :global(a) {
		color: var(--brand-primary);
		text-decoration: none;
		border-bottom: 1px solid transparent;
		transition: all 0.15s ease;
	}

	.doc-page__prose :global(a:hover) {
		border-bottom-color: var(--brand-primary);
	}

	.doc-page__prose :global(strong) {
		font-weight: 600;
		color: var(--text-primary);
	}

	/* Plain quotes only — callouts (.docs-callout) are styled by markdown.css */
	.doc-page__prose :global(blockquote.docs-quote) {
		margin: 1.5rem 0;
		padding: 0.75rem 1rem;
		background: var(--bg-secondary);
		border-left: 3px solid var(--brand-primary);
		border-radius: 0 4px 4px 0;
		color: var(--text-secondary);
		font-size: 0.9375rem;
	}

	.doc-page__prose :global(:not(pre) > code) {
		padding: 0.125rem 0.25rem;
		background: var(--bg-secondary);
		color: var(--text-primary);
		font-size: 0.8125rem;
		border-radius: 3px;
		font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
	}

	@media (max-width: 1200px) {
		.doc-page__container {
			flex-direction: column;
		}
	}
</style>
